import { useState, useMemo } from 'react';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import {
  Container,
  Grid,
  Typography,
  Stack,
  Button,
  Card,
  CardHeader,
  CardContent,
  CircularProgress,
  Alert,
  Box
} from '@material-ui/core';
import { useParams, Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Page from '../components/Page';
import Breadcrumb from '../components/common/Breadcrumb';
import CompanyInvoiceCard from '../components/billing/CompanyInvoiceCard';
import TeamPartNumbersTable from '../components/billing/TeamPartNumbersTable';
import MonthPicker, {
  formatMonthParam,
  initialMonthFromUrl
} from '../components/billing/MonthPicker';
import RefreshInvoiceButton from '../components/billing/RefreshInvoiceButton';
import useCompanyBillingReport from '../hooks/useCompanyBillingReport';
import ExportButton from '../components/billing/ExportButton';

export default function CompanyBillingReport() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  // Seed the picker from the URL `?month=YYYY-MM` so a deep link (or a click
  // through from a page where the user already picked a non-current month)
  // lands on the requested month rather than snapping back to the current one.
  const [searchParams] = useSearchParams();
  const [selectedMonth, setSelectedMonth] = useState(() => initialMonthFromUrl(searchParams));
  const { company, loading, error, invoiceNotFound, refetch } = useCompanyBillingReport({
    companyId,
    month: selectedMonth
  });

  // `MonthPicker` returns a Date; render it in the same "Month YYYY" shape
  // the rest of the page expects (matches the prior MonthPicker labels).
  const billingMonthLabel = selectedMonth ? format(selectedMonth, 'MMMM yyyy') : '';
  // Verbose day-range label for the Invoice Summary card (e.g. "March 1 - March 31")
  // — gives the user the actual span of the billing period, not just the month name.
  const billingPeriodLabel = selectedMonth
    ? `${format(startOfMonth(selectedMonth), 'LLL d')} - ${format(
        endOfMonth(selectedMonth),
        'LLL d'
      )}`
    : '';
  // Same month as the user’s “today” — drives the dynamic card title below.
  const isCurrentMonth = selectedMonth
    ? selectedMonth.getFullYear() === new Date().getFullYear() &&
      selectedMonth.getMonth() === new Date().getMonth()
    : false;
  const summaryTitle = isCurrentMonth ? 'Month-To-Date Cost Report' : 'Company Bill Summary';
  // `CompanyInvoiceCard` formats its own caption via `formatMonthLabel`, which
  // expects YYYY-MM — pass the param-shaped value so it doesn't fall through
  // the split and render "undefined August 2026".
  const billingMonthParam = formatMonthParam(selectedMonth) || '';

  // Derive the inclusive first/last day of the selected month for the
  // Refresh Invoice POST (`start_date` / `end_date`).
  const monthStartDate = selectedMonth ? format(startOfMonth(selectedMonth), 'yyyy-MM-dd') : '';
  const monthEndDate = selectedMonth ? format(endOfMonth(selectedMonth), 'yyyy-MM-dd') : '';

  // When the invoice hasn't been generated yet for the selected month,
  // the hook surfaces `invoiceNotFound: true` and `company` is null (or
  // holds the previous month's data). To keep the page looking like a
  // normal billing report, synthesize a minimal stub from the URL
  // companyId with empty/zero invoice fields. The cards and teams table
  // render unchanged — they just show empty values.
  const showInvoiceNotFound = invoiceNotFound && !loading;
  const displayCompany = useMemo(() => {
    if (company) return company;
    if (!showInvoiceNotFound) return null;
    return {
      id: companyId,
      name: companyId,
      // Empty/zero invoice-specific fields — the page still renders the
      // same layout, just with blank values where the invoice data would
      // normally appear. `partNumbers: []` keeps the part-numbers table
      // mounted so the user sees its empty state on a 404.
      invoiceNumber: '',
      monthlyCharges: 0,
      platformFee: 0,
      teamUsageCost: 0,
      teamsCount: 0,
      billableResources: 0,
      partNumbers: [],
      teams: []
    };
  }, [company, showInvoiceNotFound, companyId]);

  // Part numbers are pre-aggregated by the hook across every team for this
  // company — one row per part number with totals. Falls back to empty array
  // when the API doesn't return one (e.g. on 404 stub).
  const partNumbers = useMemo(() => displayCompany?.partNumbers || [], [displayCompany]);

  // Click a row on the part-numbers table to drill into a company-wide
  // deployments view for that part number. The destination URL is
  // company-scoped (`/billing/company/:companyId/part/:partNumber`) so the
  // breadcrumb chain on the deployments page is Home → Billing → Company →
  // Part Number — no team layer in between, since this view aggregates
  // deployments across every team for the company. The selected month is
  // carried via `?month=YYYY-MM` so the destination's MonthPicker initializes
  // to the same month the user was viewing here — without it, the picker
  // resets to the current month and the page fetches a different invoice.
  const handlePartNumberClick = (row) => {
    const monthParam = formatMonthParam(selectedMonth);
    const target = `/billing/company/${companyId}/part/${encodeURIComponent(row.id)}`;
    navigate(monthParam ? `${target}?month=${monthParam}` : target);
  };

  // Error path runs before the loading guard: if the API has reported a
  // real failure, the user should see the error alert regardless of
  // whether `company` happens to be null. Otherwise the loading spinner
  // (or "Company not found") short-circuits on a null `displayCompany`
  // and hides the actual problem.
  //
  // 404s from /billing/companies/:id/billing-report are surfaced as a
  // not-found invoice (page renders the chrome with empty fields and a
  // Refresh Invoice button). `useVrackApi` also sets its own `error` for
  // the 404, but that's expected — only surface a real error alert when
  // it's *not* the 404 case so the user gets the recovery affordance,
  // not the generic Retry alert.
  if (error && !invoiceNotFound) {
    return (
      <Page title="Error">
        <Container maxWidth={false}>
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load billing data.{' '}
            <Typography
              component="span"
              sx={{ cursor: 'pointer', textDecoration: 'underline', color: 'primary.main' }}
              onClick={refetch}
            >
              Retry
            </Typography>
          </Alert>
          <Button component={RouterLink} to="/billing" startIcon={<ChevronLeftIcon />}>
            Back to Billing
          </Button>
        </Container>
      </Page>
    );
  }

  if (loading && !displayCompany) {
    return (
      <Page title="Loading...">
        <Container maxWidth={false}>
          <Stack alignItems="center" sx={{ py: 8 }}>
            <CircularProgress />
          </Stack>
        </Container>
      </Page>
    );
  }

  // Genuinely no company AND no 404 — the company doesn't exist at all.
  // A 404 against /billing/companies/:id/billing-report still means the
  // company exists (it's listed under /companies) and we should fall
  // through to the chrome-with-banner branch.
  if (!displayCompany && !loading && !invoiceNotFound) {
    return (
      <Page title="Company Not Found">
        <Container maxWidth={false}>
          <Typography variant="h4">Company not found</Typography>
          <Button component={RouterLink} to="/billing" startIcon={<ChevronLeftIcon />}>
            Back to Billing
          </Button>
        </Container>
      </Page>
    );
  }

  return (
    <Page title={`${displayCompany.name} Billing Report | Virtual Racks`}>
      <Container maxWidth={false}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Breadcrumb
              labels={{ [displayCompany.name]: displayCompany.name }}
              // `company` is a route delimiter on /billing/company/:companyId —
              // drop it from the trail so the crumb isn't clickable. The
              // {companyId} segment still renders as the entity crumb.
              exclude={['company']}
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="overline" color="primary">
                  Billing Report
                </Typography>
                <Typography variant="h4">{displayCompany.name}</Typography>
              </Box>
              <Stack direction="row" spacing={2} alignItems="center">
                <RefreshInvoiceButton
                  companyId={companyId}
                  startDate={monthStartDate}
                  endDate={monthEndDate}
                  onRefreshed={refetch}
                />
                <ExportButton invoiceData={company} />
                <MonthPicker value={selectedMonth} onChange={setSelectedMonth} />
              </Stack>
            </Stack>
          </Grid>

          {/* When the invoice isn't found, the same cards and part-numbers
              table still render — they just show empty/zero values driven
              by the stub company. A small banner sits above the cards so
              the user knows why the fields are blank and that Refresh
              Invoice will populate them. */}
          {showInvoiceNotFound && (
            <Grid item xs={12}>
              <Alert severity="info" data-testid="invoice-not-found-alert">
                No invoice has been generated for {displayCompany.name} in {billingMonthLabel}. Use
                Refresh Invoice to generate one — the page will populate as soon as the invoice is
                available.
              </Alert>
            </Grid>
          )}

          {/* Company invoice summary + Invoice card */}
          <Grid container item xs={12} spacing={3}>
            <Grid item xs={12} md={7}>
              <Card sx={{ height: '100%' }}>
                <CardHeader title={summaryTitle} subheader="Official company-level billing view" />
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Company
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                        {displayCompany.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Billing Period
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                        {billingPeriodLabel}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={5}>
              <CompanyInvoiceCard company={displayCompany} billingMonth={billingMonthParam} />
            </Grid>
          </Grid>

          {/* Company-level billable parts — aggregated across all teams for
              this company. One row per part number; clicking a row drills
              into the company-scoped deployments page for that part
              number, which aggregates deployments across every team. */}
          <Grid item xs={12}>
            <TeamPartNumbersTable
              partNumbers={partNumbers}
              onPartNumberClick={handlePartNumberClick}
            />
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}
